/**
 * **版面画像（LayoutProfile）—— 从视口推导出的“这一屏用哪个设计锚”。**
 *
 * 只有**两个锚**：横 `1280×800`、竖 `800×1280`（用户拍板：不新增第三个尺寸）。
 * 视口比锚更宽/更高时，整块逻辑画布等比缩放（contain）居中，比例永不变形。
 *
 * 安全区（刘海 / 手势条）用 CSS `env(safe-area-inset-*)` 量出来，再**除以 scale**
 * 换成逻辑画布坐标，界面直接按它让位。
 *
 * 这一层是纯函数（不碰 window），DOM 侧的安全区读取在 `safeArea.ts`。
 */

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** 四边安全区（CSS px，DOM 侧原样读出） */
export interface SafeInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const ZERO_INSETS: SafeInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/** 横版设计锚 */
export const LANDSCAPE = { width: 1280, height: 800 } as const;
/** 竖版设计锚 */
export const PORTRAIT = { width: 800, height: 1280 } as const;

export type Orientation = 'landscape' | 'portrait';

export interface LayoutProfile {
  readonly orientation: Orientation;
  /** 逻辑画布尺寸（两个锚之一） */
  readonly width: number;
  readonly height: number;
  readonly viewport: Viewport;
  /** 逻辑画布 → CSS 像素的缩放（contain） */
  readonly scale: number;
  /** 安全区，**已经换算成逻辑画布坐标** */
  readonly safe: SafeInsets;
}

/** 视口偏高（height > width）就用竖锚，否则横锚 */
export function orientationOf(viewport: Viewport): Orientation {
  return viewport.height > viewport.width ? 'portrait' : 'landscape';
}

export function anchorFor(viewport: Viewport): { width: number; height: number } {
  return orientationOf(viewport) === 'portrait' ? PORTRAIT : LANDSCAPE;
}

function safeDiv(value: number, scale: number): number {
  return scale > 0 ? value / scale : 0;
}

/**
 * ⚠️ 用户拍板：**竖屏只给“请横屏”提示，不在竖屏游玩**（见 `rotateGate`）。
 * 所以渲染锚**恒为横版** `LANDSCAPE`；`anchorFor()`/`PORTRAIT` 保留给将来
 * 真要竖屏游玩时用。`orientation` 仍然照实报告，供“要不要显示旋转提示”判断。
 */
export function computeLayout(
  viewport: Viewport,
  insets: SafeInsets = ZERO_INSETS,
  anchor: { readonly width: number; readonly height: number } = LANDSCAPE,
): LayoutProfile {
  const scale = Math.min(viewport.width / anchor.width, viewport.height / anchor.height);
  return {
    orientation: orientationOf(viewport),
    width: anchor.width,
    height: anchor.height,
    viewport,
    scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
    safe: {
      top: safeDiv(insets.top, scale),
      right: safeDiv(insets.right, scale),
      bottom: safeDiv(insets.bottom, scale),
      left: safeDiv(insets.left, scale),
    },
  };
}

/** 两份版面“实际上是同一个”吗（用来决定要不要重建界面）。缩放留一点容差 */
export function sameLayout(a: LayoutProfile, b: LayoutProfile): boolean {
  if (a.orientation !== b.orientation || a.width !== b.width || a.height !== b.height) return false;
  if (Math.abs(a.scale - b.scale) > 0.001) return false;
  return (
    Math.abs(a.safe.top - b.safe.top) < 0.5 &&
    Math.abs(a.safe.right - b.safe.right) < 0.5 &&
    Math.abs(a.safe.bottom - b.safe.bottom) < 0.5 &&
    Math.abs(a.safe.left - b.safe.left) < 0.5
  );
}
